import { Fragment, useState } from "react";
import { Link } from "react-router-dom";

import { useTraderScanChanges } from "../api/queries";
import type { TraderScanChange } from "../api/types";
import "./pages.css";
import { Empty } from "../components/Empty";
import { ModePill } from "../components/ModePill";
import { Panel } from "../components/Panel";
import { Skeleton } from "../components/Skeleton";
import { TitleBand } from "../components/TitleBand";
import { Variation } from "../components/Variation";
import { adena, plural, signed, when } from "../format";
import { itemHref } from "../search/nav";
import { useTrader } from "./Trader";
import { listingKey } from "./traderSort";

/** The summary line, or why there is not one.
 *
 *  A first appearance has nothing to compare against, and printing
 *  "0 new, 0 gone, 0 repriced" there would claim nothing changed — a different
 *  and much stronger statement than "we do not know". The same holds per mode:
 *  a walk that covered a side for the first time contributes nothing rather
 *  than zeroes. */
function Summary({ scan }: { scan: TraderScanChange }) {
  const compared = Object.values(scan.compared_to).filter((uid) => uid !== null);
  if (compared.length === 0) {
    return <span className="dim">first appearance here</span>;
  }
  const parts = [
    scan.added ? `+${scan.added} new` : "",
    scan.gone ? `${scan.gone} gone` : "",
    scan.repriced ? `${scan.repriced} repriced` : "",
  ].filter(Boolean);
  return parts.length === 0
    ? <span className="dim">no change</span>
    : <span>{parts.join(" · ")}</span>;
}

const KIND_LABELS: Record<string, string> = {
  added: "new", gone: "gone", repriced: "repriced", unreadable: "unreadable",
};

function Changes({ nick, scanUid }: { nick: string; scanUid: string }) {
  const changes = useTraderScanChanges(nick, scanUid);
  if (changes.isError) return <Empty>Could not load this scan’s changes.</Empty>;
  if (!changes.data) return <Skeleton rows={3} />;
  if (changes.data.changes.length === 0) return <Empty>Nothing changed.</Empty>;

  return (
    <div className="scroll">
      <table>
        <thead>
          <tr>
            <th>Item</th>
            <th>Mode</th>
            <th>What</th>
            <th className="num">Was</th>
            <th className="num">Now</th>
            <th className="num">Moved</th>
          </tr>
        </thead>
        <tbody>
          {changes.data.changes.map((change) => (
            <tr key={`${change.kind}-${listingKey(change)}`}>
              <td>
                <Link to={itemHref(change.item_id, change.item)}>{change.item}</Link>
                <Variation {...change} />
              </td>
              <td><ModePill mode={change.mode} /></td>
              <td className="dim">{KIND_LABELS[change.kind] ?? change.kind}</td>
              <td className="num dim">{adena(change.previous)}</td>
              <td className="num">{adena(change.price)}</td>
              <td className="num">
                {/* Only a reprice has a movement. An unreadable price is not a
                    fall to nothing, and a listing that appeared or vanished
                    moved no price at all. */}
                {change.kind === "repriced"
                 && change.price !== null && change.previous !== null
                  ? signed(change.price - change.previous)
                  : <span className="dim">{adena(null)}</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function TraderHistory() {
  const trader = useTrader();
  const [open, setOpen] = useState<string | null>(null);

  return (
    <>
      <TitleBand title="History" note={plural(trader.scans, "appearance")} />
      <Panel primary>
        {trader.scans_truncated ? (
          <p className="scan-meta">showing the 50 most recent scans</p>
        ) : null}
        {trader.history.length === 0 ? (
          <Empty>No scans recorded for this trader.</Empty>
        ) : (
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  {/* Unlabelled on purpose: the column holds one disclosure
                      button per row, each of which names the scan it opens. A
                      header word here would be read before every one of them
                      and say nothing the button does not. */}
                  <th />
                  <th>When</th>
                  <th>Town</th>
                  <th className="num">Offers</th>
                  <th>Changed</th>
                </tr>
              </thead>
              <tbody>
                {trader.history.map((scan) => (
                  <Fragment key={scan.scan_uid}>
                    <tr>
                      <td>
                        <button
                          type="button"
                          className="disclose"
                          aria-expanded={open === scan.scan_uid}
                          aria-label={`Changes in the scan of ${when(scan.started_at)}`}
                          onClick={() =>
                            setOpen(open === scan.scan_uid ? null : scan.scan_uid)}
                        >
                          {open === scan.scan_uid ? "−" : "+"}
                        </button>
                      </td>
                      <td>
                        <Link to={`/scans/${scan.scan_uid}`}>{when(scan.started_at)}</Link>
                      </td>
                      <td>{scan.city || <span className="dim">{adena(null)}</span>}</td>
                      <td className="num">{scan.offers}</td>
                      <td><Summary scan={scan} /></td>
                    </tr>
                    {open === scan.scan_uid ? (
                      <tr>
                        <td colSpan={5}>
                          <Changes nick={trader.nick} scanUid={scan.scan_uid} />
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </>
  );
}
